import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import PropTypes from 'prop-types';
import api from "api/axiosConfig";
import API_BASE from "configs/hostConfig";
import { Formik, Form, Field } from 'formik';
import {
  Flex,
  Box,
  Button,
  Container,
  FormControl,
  Input,
  Heading,
  Text,
  Textarea,
  VStack,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  useDisclosure,
  useToast,
  useBreakpointValue,
} from '@chakra-ui/react';
import { TbAlertCircle } from 'react-icons/tb';

import theme from 'ui/theme/theme';
import InactiveBanner from './components/InactiveBanner';
import { essay_schema } from './data/essay_schema';

import {
  getWholeWords,
  getFocusedSentences,
  getFocusedParagraphs,
} from 'utils/StringUtils';
import SuggestionBox from './components/SuggestionBox';
import HighlightedText from 'components/HighlightedText';
import { instr_editing } from 'utils/data/constants';
import SmallScreenAlert from './components/SmallScreenAlert';

function EditingForm({
  breadcrumbProg,
  setBreadcrumbProg,
  nextPath,
  returnUrl,
  isPlanActive,
}) {
  // console.log('---------entering editing page');

  const navigate = useNavigate();
  const { essayId } = useParams();
  const isNarrow = useBreakpointValue({ base: true, md: true, lg: false });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [currEdition, setCurrEdition] = useState('');
  const [wordCount, setWordCount] = useState(0);
  const [exerciseData, setExerciseData] = useState({});
  const [enableNext, setEnableNext] = useState(false);
  const [selectedText, setSelectedText] = useState('');
  const [focusedSentence, setFocusedSentence] = useState('');
  const [focusedParagraph, setFocusedParagraph] = useState('');
  const [messages, setMessages] = useState([]);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const textareaRef = useRef();
  const toast = useToast();

  const essayDetails = {
    ...essay_schema,
    ...JSON.parse(localStorage.getItem('essayExerciseDetails')),
  };

  function updateStateData(details) {
    setCurrEdition(details?.essayText);
    setExerciseData(details);
    setEnableNext(details.progress > 5 ? true : false);
    handleWordCount(details?.essayText);
  }

  useEffect(() => {
    setBreadcrumbProg(breadcrumbProg);
  }, [setBreadcrumbProg, breadcrumbProg]);

  useEffect(() => {
    if (essayDetails.essayId == essayId) {
      updateStateData(essayDetails);
    }
    if (
      essayDetails.essayId != essayId ||
      (essayDetails.progress >= 5 && !essayDetails?.editText)
    ) {
      api
        .get(`${API_BASE}/api/essay-exercise/retrieve-data?exerciseId=${essayId}`)
        .then((res) => {
          // console.log('========= edition fetch response: ', res);
          if (res.status == 200) {
            const newExerciseData = res.data?.data;
            updateStateData(newExerciseData);
            localStorage.setItem(
              'essayExerciseDetails',
              JSON.stringify(newExerciseData)
            );
          }
        })
        .catch(() =>
          console.log('failed to fetch exercise editing data, id: ', essayId)
        );
    }
  }, []);

  useEffect(() => {
    api
      .get(`${API_BASE}/api/essay-exercise/editing-suggestions?exerciseId=${essayId}`)
      .then((res) => {
        if (res.status == 200) {
          setMessages(res.data?.data);
        }
      });
  }, [essayId]);

  const handleWordCount = (text) => {
    const count = text?.trim() ? text.trim().split(/\s+/).length : 0;
    setWordCount(count);
  };

  const handleTextSelection = () => {
    const textarea = textareaRef.current;
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;

    const selected = textarea.value.substring(start, end);

    if (selected) {
      const adjustedSelection = getWholeWords(textarea.value, start, end);
      setSelectedText(adjustedSelection);

      const focusContext = getFocusedSentences(textarea.value, start, end);
      setFocusedSentence(focusContext);

      const paragraphContext = getFocusedParagraphs(textarea.value, start, end);
      setFocusedParagraph(paragraphContext);

      onOpen(); // Open the modal
    }
  };

  function updateRevison(values, submitType) {
    if (!values?.draft || values.draft.length < 20) {
      const warning =
        'Your submission is a bit brief. We know you have great ideas to share!';
      toastMessage(warning);
      return;
    }
    setIsSubmitting(true);
    api
      .patch(`${API_BASE}/api/essay-exercise/revisions`, {
        ...values,
        updateType: submitType,
        isEditing: true,
      })
      .then((res) => {
        // console.log('=======patch revisions response: ', res);
        if (res.status == 200) {
          localStorage.setItem(
            'essayExerciseDetails',
            JSON.stringify(res.data.data)
          );
          if (submitType === 2) {
            navigate(nextPath);
          }
        }
      })
      .catch(() => alert('Failed to update.'))
      .finally(() => setIsSubmitting(false));
  }

  const handleProofRead = async (selected, focused, paragraph, type) => {
    if (type == 2) {
      selected = focused;
      focused = paragraph;
    }
    const body = {
      exerciseId: essayId,
      title: exerciseData?.title,
      targetText: selected,
      textArea: focused,
      grade: exerciseData?.level,
      essayType: exerciseData?.secondaryType || exerciseData?.primaryType,
      type,
    };
    setIsSubmitting(true);
    api
      .post(`${API_BASE}/api/essay-exercise/proofread`, body)
      .then((res) => {
        // console.log('======= proof reading res: ', res);
        if (res.status == 200) {
          const proofread = {
            targetText: selected,
            textArea: focused,
            comment: res.data?.data,
          };
          updateProofreadMessages(proofread);
        }
      })
      .catch(() => alert('Ivy is busy.'))
      .finally(() => {
        setIsSubmitting(false);
        onClose();
      });
  };

  function updateProofreadMessages(suggestion) {
    let newMessages = [];
    messages.forEach((msg) => newMessages.push(msg));
    newMessages.push(suggestion);
    setMessages(newMessages);
  }

  function toastMessage(msg) {
    toast({
      position: 'top',
      render: () => (
        <Box
          border="1px solid"
          // color={theme.colors.semantics['warning-light']}
          borderColor={theme.colors.semantics.warning}
          p={4}
          bg={theme.colors.semantics['warning-light']}
          borderRadius={12}
          wordBreak="break-all"
        >
          <Flex>
            <TbAlertCircle color={theme.colors.semantics.warning} />
            <Flex ml="12px" flex="1">
              <Text fontWeight="bold" ml="2">
                {msg}
              </Text>
            </Flex>
          </Flex>
        </Box>
      ),
      duration: 3000,
    });
  }

  if (isNarrow) {
    return <SmallScreenAlert />;
  }

  return (
    <Container width="100%" maxW="none" py={2} mb="4px">
      {!isPlanActive && <InactiveBanner />}
      <Flex height="calc(100vh - 68px)" overflow="hidden">
        <Box
          flex="2" // 70% of the width
          overflowY="auto" // Independent scroll for this section
          pl={4}
          pt={2}
          pb={4}
          borderRight="1px solid #e0e0e0"
          bg="gray.50"
        >
          <Formik
            initialValues={{
              title: exerciseData?.title,
              draft: currEdition,
              exerciseId: essayId,
            }}
            enableReinitialize={true}
            onSubmit={() => { }}
          >
            {({ values, handleChange }) => (
              <Form>
                <VStack spacing={4} align="stretch">
                  <Heading size="md" color="teal.700">
                    Editing
                  </Heading>
                  {/* Title Input */}
                  <Box bg="white">
                    <FormControl>
                      {/* <FormLabel htmlFor="title">Title</FormLabel> */}
                      <Field
                        as={Input}
                        id="title"
                        name="title"
                        placeholder="Enter the title"
                        size="lg"
                        fontWeight="bold"
                      />
                    </FormControl>

                    {/* Draft Textarea */}
                    <FormControl>
                      {/* <FormLabel htmlFor="draft">Draft</FormLabel> */}
                      <Textarea
                        // as={Textarea}
                        id="draft"
                        name="draft"
                        placeholder="Enter your draft"
                        size="lg"
                        height="calc(100vh - 240px)"
                        // rows={30}
                        // minHeight="30em"
                        ref={textareaRef}
                        value={values.draft}
                        onChange={(e) => {
                          handleChange(e);
                          handleWordCount(e.target.value);
                        }}
                        onMouseUp={handleTextSelection} // Trigger on text selection
                      />
                    </FormControl>
                  </Box>

                  {/* Modal to display selected text and focused context */}
                  <Modal isOpen={isOpen} onClose={onClose}>
                    <ModalOverlay />
                    <ModalContent>
                      <ModalHeader>Evaluate Selected Text</ModalHeader>
                      <ModalCloseButton />
                      <ModalBody>
                        <HighlightedText
                          words={selectedText}
                          sentence={focusedSentence}
                          paragraph={focusedParagraph}
                        />
                      </ModalBody>
                      <ModalFooter>
                        <Button
                          colorScheme="green"
                          mr={4}
                          isDisabled={isSubmitting || !isPlanActive}
                          isLoading={isSubmitting}
                          onClick={() =>
                            handleProofRead(
                              selectedText,
                              focusedSentence,
                              focusedParagraph,
                              1
                            )
                          }
                        >
                          Evaluate words
                        </Button>
                        <Button
                          colorScheme="green"
                          isDisabled={isSubmitting || !isPlanActive}
                          isLoading={isSubmitting}
                          onClick={() =>
                            handleProofRead(
                              selectedText,
                              focusedSentence,
                              focusedParagraph,
                              2
                            )
                          }
                        >
                          Evaluate sentences
                        </Button>
                      </ModalFooter>
                    </ModalContent>
                  </Modal>
                  <Box>
                    <Flex align="center" justify="space-between" w="100%">
                      <Text color="gray.500">Word Count: {wordCount}</Text>
                      <Flex gap={2} mr={4}>
                        <Button
                          colorScheme="gray"
                          type="button"
                          variant="link"
                          onClick={() => navigate(returnUrl)}
                        >
                          Back
                        </Button>
                        {enableNext ? (
                          <Button
                            colorScheme="green"
                            type="button"
                            onClick={() => navigate(nextPath)}
                          >
                            Next
                          </Button>
                        ) : (
                          <>
                            <Button
                              colorScheme="green"
                              variant="outline"
                              onClick={() => updateRevison(values, 0)}
                              isDisabled={isSubmitting}
                              isLoading={isSubmitting}
                              // isDisabled={!isValid || isSubmitting}
                              type="button"
                            >
                              Save for later
                            </Button>

                            <Button
                              colorScheme="green"
                              onClick={() => updateRevison(values, 2)}
                              // isDisabled={!isValid || isSubmitting}
                              isDisabled={isSubmitting}
                              isLoading={isSubmitting}
                              type="button"
                            >
                              Submit
                            </Button>
                          </>
                        )}
                      </Flex>
                    </Flex>
                  </Box>
                </VStack>
              </Form>
            )}
          </Formik>
        </Box>
        <SuggestionBox messages={messages} preMessage={instr_editing} />
      </Flex>
    </Container>
  );
}

EditingForm.propTypes = {
  breadcrumbProg: PropTypes.number,
  setBreadcrumbProg: PropTypes.func,
  nextPath: PropTypes.string,
  returnUrl: PropTypes.string,
  isPlanActive: PropTypes.bool,
};

export default EditingForm;
